import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ASSETS, HERO_SLIDES } from "@/data/content";

const AUTOPLAY_MS = 6000;

export const HeroRotator = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const goTo = useCallback((i) => {
    setIndex((i + HERO_SLIDES.length) % HERO_SLIDES.length);
  }, []);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  useEffect(() => {
    if (paused) return undefined;
    timerRef.current = setTimeout(() => {
      setIndex((i) => (i + 1) % HERO_SLIDES.length);
    }, AUTOPLAY_MS);
    return () => clearTimeout(timerRef.current);
  }, [index, paused]);

  const scrollToAudit = (e) => {
    e.preventDefault();
    const el = document.getElementById("visibility-audit");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const scrollToTarget = (e, target) => {
    e.preventDefault();
    const el = document.querySelector(target);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const slide = HERO_SLIDES[index];

  return (
    <section
      data-testid="hero-rotator"
      className="relative overflow-hidden bg-[#0b0b0c] hero-noise"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="Slingshot SEO highlights"
    >
      {/* Backgrounds (cross-fade) */}
      {HERO_SLIDES.map((s, i) => (
        <div
          key={s.key}
          className="absolute inset-0 transition-opacity duration-700 ease-out"
          style={{ opacity: i === index ? 1 : 0 }}
          aria-hidden={i !== index}
        >
          <img
            src={s.bg}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(2,2,2,0.82) 0%, rgba(2,2,2,0.58) 45%, rgba(2,2,2,0.82) 100%)",
            }}
          />
        </div>
      ))}

      {/* Content */}
      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 min-h-[640px] md:min-h-[700px] flex items-center justify-center pt-32 pb-20 md:pt-36 md:pb-24">
        <AnimatePresence mode="wait">
          <motion.div
            key={slide.key}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="font-nunito text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.3em] text-[#ff6b63] mb-5">
              {slide.eyebrow}
            </p>
            <h1
              data-testid="hero-slide-title"
              className="font-nunito font-extrabold text-white text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.08] tracking-tight"
            >
              {slide.titlePre}
              <span className="text-[#ee2e24]">{slide.titleAccent}</span>
              {slide.titlePost}
            </h1>
            <p
              data-testid="hero-slide-subline"
              className="mt-5 text-[#dedede] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto"
            >
              {slide.subline}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <a
                href="#visibility-audit"
                onClick={scrollToAudit}
                data-testid="hero-primary-cta-button"
                className="w-full sm:w-auto max-w-[340px] inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_14px_40px_rgba(238,46,36,0.35)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[rgba(238,46,36,0.5)] focus:ring-offset-2 focus:ring-offset-black"
              >
                {slide.ctaPrimary}
              </a>
              <a
                href={slide.ctaSecondaryTarget}
                onClick={(e) => scrollToTarget(e, slide.ctaSecondaryTarget)}
                data-testid="hero-secondary-cta-button"
                className="w-full sm:w-auto max-w-[340px] inline-flex justify-center items-center rounded-[10px] border border-white/40 px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white hover:bg-white/10 active:scale-[0.98] transition-colors duration-150"
              >
                {slide.ctaSecondary}
              </a>
            </div>

            {/* Trust badges — locked brand assets */}
            <div data-testid="hero-trust-badges" className="mt-9 flex items-center justify-center gap-6">
              <img
                src={ASSETS.badgeRecommended}
                alt="Slingshot SEO recommended company"
                className="h-16 sm:h-20 w-auto drop-shadow-[0_8px_20px_rgba(0,0,0,0.5)]"
                loading="lazy"
              />
              <img
                src={ASSETS.badgeInc500}
                alt="Inc. 500 List"
                className="h-16 sm:h-20 w-auto drop-shadow-[0_8px_20px_rgba(0,0,0,0.5)]"
                loading="lazy"
              />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Arrows (Flatsome-style circles) */}
      <button
        type="button"
        onClick={prev}
        data-testid="hero-slider-prev-button"
        aria-label="Previous slide"
        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-white/35 text-white/85 flex items-center justify-center hover:bg-white/10 hover:border-white/60 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-white/40"
      >
        <ChevronLeft size={22} strokeWidth={2} />
      </button>
      <button
        type="button"
        onClick={next}
        data-testid="hero-slider-next-button"
        aria-label="Next slide"
        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full border border-white/35 text-white/85 flex items-center justify-center hover:bg-white/10 hover:border-white/60 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-white/40"
      >
        <ChevronRight size={22} strokeWidth={2} />
      </button>

      {/* Dots */}
      <div data-testid="hero-slider-dots" className="absolute bottom-5 left-0 right-0 flex items-center justify-center gap-2.5">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.key}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === index ? "true" : "false"}
            data-testid={`hero-slider-dot-${i + 1}`}
            className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${
              i === index ? "w-7 bg-[#ee2e24]" : "w-2.5 bg-white/45 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
};
